import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  OnChanges,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { UpdateNotificationEmailDto } from "../../../core/models/project-workflow/updateNotificationEmailDto";
import { UpdateNotificationSettingsDto } from "../../../core/models/project-workflow/updateNotificationSettingsDto";
import { WorkflowNotificationSettings } from "../../../core/models/project-workflow/workflownotificationSettingDto";
import { LoaderService } from "../../../core/services/loader.service";
import { ProjectWorkflowService } from "../../../core/services/project-workflow.service";
import { EnumPackageStatusType } from "../../../shared/enums/packageStatusType";
import { EnumRecipientsType } from "../../../shared/enums/recipentType";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { DialogModule } from "primeng/dialog";
import { CommonModule } from "@angular/common";
import { TabsModule } from "primeng/tabs";
import { NotificationEmailSettingComponent } from "../notification-email-setting/notification-email-setting.component";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { CheckboxModule } from "primeng/checkbox";

@Component({
  selector: "jni-notification-settings",
  templateUrl: "./notification-settings.component.html",
  styleUrls: ["./notification-settings.component.scss"],
  imports: [
    DialogModule,
    CommonModule,
    ReactiveFormsModule,
    TabsModule,
    NotificationEmailSettingComponent,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
  ],
  standalone: true,
})
export class NotificationSettingsComponent
  implements OnInit, AfterViewInit, OnChanges
{
  @Input() viewSettingsModal = false;
  @Input() workflowId = 0;
  @Input() notificationSettings = [] as WorkflowNotificationSettings[];
  @Input() isGlobal = false;
  @Input() lineRecIdD365 = 0;
  @Output() closeUpdateSettingModalEvent = new EventEmitter();
  @Output() dailogHideEventSettings = new EventEmitter<boolean>();
  @Output() refreshDataEvent = new EventEmitter();
  @Output() hasUnsavedChangesEvent = new EventEmitter<boolean>();

  notificationSettingForm!: FormGroup;
  viewUpdateEmailTemplateModal = false;

  EnumRecipentsType = EnumRecipientsType;
  EnumPackageStatusType = EnumPackageStatusType;

  workflowEmailDetails = {} as UpdateNotificationEmailDto;

  constructor(
    private _formBuilder: FormBuilder,
    private _workflowService: ProjectWorkflowService,
    private _toastr: JniToastrService,
    private _loaderService: LoaderService
  ) {
    this.initializeForm();
  }

  ngOnInit() {
    this.populateSettingsData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["notificationSettings"]) {
      this.populateSettingsData();
    }
  }

  initializeForm() {
    this.notificationSettingForm = this._formBuilder.group({
      workflowOwnerSettings: this.createSettingsGroup(),
      coOwnerSettings: this.createSettingsGroup(),
      //adminSettings: this.createSettingsGroup(),
      clientSettings: this.createSettingsGroup({
        clientEmailRecipients: this._formBuilder.control("", [
          Validators.maxLength(2048),
        ]),
      }),
      chatEmailEnabled: [false],
    });
  }

  createSettingsGroup(
    additionalControls?: Record<
      string,
      import("@angular/forms").AbstractControl
    >
  ) {
    const group = this._formBuilder.group({
      id: [0],
      projectWorkflowId: [0],
      recipientType: [0],
      shippingEmail: [false],
      transitEmail: [false],
      deliveryEmail: [false],
      exceptionEmail: [false],
      shippingTemplate: [""],
      transitTemplate: [""],
      deliveryTemplate: [""],
      exceptionTemplate: [""],
      shippingEmailSubject: [""],
      transitEmailSubject: [""],
      deliveryEmailSubject: [""],
      exceptionEmailSubject: [""],
    }) as FormGroup;

    if (additionalControls) {
      for (const controlName in additionalControls) {
        group.addControl(controlName, additionalControls[controlName]);
      }
    }

    return group;
  }

  populateSettingsData() {
    this.notificationSettings.forEach(obj => {
      const settings = {
        id: obj.id,
        projectWorkflowId: obj.projectWorkflowId,
        recipientType: obj.recipientType,
        shippingEmail: obj.shippingEmail,
        transitEmail: obj.transitEmail,
        deliveryEmail: obj.deliveryEmail,
        exceptionEmail: obj.exceptionEmail,
        shippingTemplate: obj.shippingTemplate,
        transitTemplate: obj.transitTemplate,
        deliveryTemplate: obj.deliveryTemplate,
        exceptionTemplate: obj.exceptionTemplate,
        shippingEmailSubject: obj.shippingEmailSubject,
        transitEmailSubject: obj.transitEmailSubject,
        deliveryEmailSubject: obj.deliveryEmailSubject,
        exceptionEmailSubject: obj.exceptionEmailSubject,
      };

      switch (obj.recipientType) {
        case 1:
          this.notificationSettingForm.patchValue({
            workflowOwnerSettings: settings,
          });
          break;
        case 2:
          this.notificationSettingForm.patchValue({
            coOwnerSettings: settings,
          });
          break;
        // case 3:
        //   this.notificationSettingForm.patchValue({ adminSettings: settings });
        //   break;
        case 4:
          this.notificationSettingForm.patchValue({ clientSettings: settings });
          this.notificationSettingForm.patchValue({
            clientSettings: {
              clientEmailRecipients: obj.clientEmailRecepients ?? "",
            },
          });
          break;
      }
    });

    this.notificationSettingForm.patchValue({
      chatEmailEnabled: !(
        this.notificationSettings[0]?.chatEmailEnabled ?? false
      ),
    });
  }

  extractFormSettingsData(): WorkflowNotificationSettings[] {
    const formValue = this.notificationSettingForm.value;
    const settingsList: WorkflowNotificationSettings[] = [];

    [
      "workflowOwnerSettings",
      "coOwnerSettings",
      "adminSettings",
      "clientSettings",
    ].forEach(groupName => {
      const settings = formValue[groupName];
      if (settings && settings.recipientType !== 0) {
        settingsList.push(settings);
      }
    });

    settingsList.forEach((obj: WorkflowNotificationSettings) => {
      obj.chatEmailEnabled = !(
        this.notificationSettingForm.get("chatEmailEnabled")?.value ?? false
      );
    });
    return settingsList;
  }

  updateNotificationSettings() {
    this._loaderService.setLoaderState(true);
    const data = this.extractFormSettingsData();
    const payload: UpdateNotificationSettingsDto = {
      notificatonSettings: data,
      isGlobal: this.isGlobal,
      projectWorkflowId: this.isGlobal ? 0 : this.workflowId,
      lineRecId: this.lineRecIdD365,
      isLines: this.lineRecIdD365 === 0 ? false : true,
    };
    this._workflowService
      .updateWorkflowNotificationSettings(payload)
      .subscribe({
        next: res => {
          if (res?.data) {
            this._toastr.success("Success", "Notification Settings Updated");
            this.hasUnsavedChangesEvent.emit(false);
            this.closeUpdateSettingModalEvent.emit();
          } else {
            this._toastr.error("Error", res?.message);
          }
          //this.refreshDataEvent.emit();
          this._loaderService.setLoaderState(false);
        },
        error: () => {
          this._loaderService.setLoaderState(false);
          this._toastr.error("Error", "Unable to Update Notification Settings");
        },
      });
  }

  closeModal() {
    this.closeUpdateSettingModalEvent.emit();
  }

  showUpdateEmailTemplate(
    recipentType: EnumRecipientsType,
    packageStatus: EnumPackageStatusType
  ) {
    this.workflowEmailDetails = {
      projectWorkflowId: this.isGlobal ? 0 : this.workflowId,
      recipientType: recipentType,
      emailType: packageStatus,
      isGlobal: this.isGlobal,
      lineRecIdD365: this.lineRecIdD365 ?? 0,
    } as UpdateNotificationEmailDto;
    const data = this.notificationSettings.find(obj => {
      if (obj.recipientType === recipentType) {
        return obj;
      }
      return;
    });

    switch (packageStatus) {
      case "Shipping":
        this.workflowEmailDetails.content = data?.shippingTemplate ?? "";
        this.workflowEmailDetails.subject = data?.shippingEmailSubject ?? "";
        this.workflowEmailDetails.Id = data?.id ?? 0;
        break;
      case "In-Transit":
        this.workflowEmailDetails.content = data?.transitTemplate ?? "";
        this.workflowEmailDetails.subject = data?.transitEmailSubject ?? "";
        this.workflowEmailDetails.Id = data?.id ?? 0;
        break;
      case "Delivery":
        this.workflowEmailDetails.content = data?.deliveryTemplate ?? "";
        this.workflowEmailDetails.subject = data?.deliveryEmailSubject ?? "";
        this.workflowEmailDetails.Id = data?.id ?? 0;
        break;
      case "Exception":
        this.workflowEmailDetails.content = data?.exceptionTemplate ?? "";
        this.workflowEmailDetails.subject = data?.exceptionEmailSubject ?? "";
        this.workflowEmailDetails.Id = data?.id ?? 0;
        break;
    }
    this.closeUpdateSettingModalEvent.emit();
    this.viewUpdateEmailTemplateModal = !this.viewUpdateEmailTemplateModal;
  }

  closeEmailUpdateModal() {
    this.dailogHideEventSettings.emit(false);
    this.viewUpdateEmailTemplateModal = false;
  }

  updateEmailNotificationSettings(event: UpdateNotificationEmailDto) {
    this._loaderService.setLoaderState(true);
    if (event) {
      this._workflowService
        .updateWorkflowEmailNotificationSetting(event)
        .subscribe({
          next: res => {
            if (res?.data) {
              this.closeEmailUpdateModal();
              //this.refreshDataEvent.emit();
              this._loaderService.setLoaderState(false);
              this._toastr.success("Success", "Notification Template updated");
              this.hasUnsavedChangesEvent.emit(false);
            } else {
              this._loaderService.setLoaderState(false);
              this._toastr.error("Error", res?.message);
            }
          },
          error: () => {
            this._loaderService.setLoaderState(false);
            this._toastr.error(
              "Error",
              "Unable to update Notification Template"
            );
          },
        });
    } else {
      this._loaderService.setLoaderState(false);
      this._toastr.error("Error", "Unable to update Notification Template");
    }
  }

  onHideSettingDailog() {
    this.dailogHideEventSettings.emit(false);
  }

  ngAfterViewInit(): void {
    this.notificationSettingForm.valueChanges.subscribe(() => {
      this.hasUnsavedChangesEvent.emit(true);
    });
  }

  onEmailInputChanges(event: boolean) {
    this.hasUnsavedChangesEvent.emit(event);
  }
}
